import React, { PureComponent, createRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { findIndex } from 'lodash';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import scaleSize from 'utils/scale-size';
import { FocusableParent } from '../navigation';

const ListContentWrapper = styled.div.attrs(({ listOffset }) => {
  return {
    style: {
      transform: `translate3d(0, -${scaleSize(listOffset)}px, 0)`,
    },
  };
})``;

class VerticalList extends PureComponent {
  listRef = createRef();

  state = {
    focusedItemIndex: this.props.defaultIndex,
  };

  componentDidMount() {
    const { defaultSection, defaultIndex, items } = this.props;
    // Set focus to default element
    if (defaultSection && items[defaultIndex]) {
      this.setFocusOnDefaultElement();
    }
  }

  componentDidUpdate(prevProps) {
    const { defaultIndex } = this.props;
    const { defaultIndex: prevDefaultIndex } = prevProps;
    const { focusedItemIndex } = this.state;
    // set focus on new default element if old default element was focused
    if (prevDefaultIndex !== defaultIndex && prevDefaultIndex === focusedItemIndex) {
      this.setState({ focusedItemIndex: defaultIndex });
    }
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.items.length === nextProps.items.length) return;
    // If count of items changed need to change focusedIndex to actual index
    const { items } = this.props;
    const { focusedItemIndex } = this.state;
    this.setState({
      focusedItemIndex: findIndex(nextProps.items, { id: items[focusedItemIndex].id }),
    });
  }

  setFocusOnDefaultElement = () => {
    const { defaultIndex, listId, items } = this.props;
    if (items[defaultIndex]) {
      SpatialNavigationWrapper.focus(`#${listId}-item-${items[defaultIndex].id}`);
    }
  };

  /**
   * This wrapper fixes a very complex bug with the SpatialNavigation library.
   * FocusableParent (which is current list) during the first render,
   * puts focus on the first physically accessible child (DOM element)
   * which is triggered "onFocus" with wrong "focusedItemIndex"
   * Call "setFocusOnDefaultElement" solve this problem and fist focus would be on
   */
  onFocus = (() => {
    let isFirstFocusCall = true;
    return (focusedItemIndex) => {
      const { onFocus, items, infinite, defaultIndex } = this.props;

      if (isFirstFocusCall && infinite && defaultIndex >= 0) {
        isFirstFocusCall = false;
        this.setFocusOnDefaultElement();
        return;
      }
      this.setState({ focusedItemIndex });
      onFocus && onFocus(items[focusedItemIndex], focusedItemIndex);
    };
  })();

  onEnter = (index) => {
    const { onEnter, items } = this.props;
    onEnter && onEnter(items[index], index);
  };

  calculateOffset() {
    const { infinite, itemHeight, renderSize, items } = this.props;
    const size = items.length;

    // Add 1 item left offset for infinite lits if size bigger renderSize
    if (infinite) return (renderSize < size ? 1 : 0) * itemHeight;

    const { focusedItemIndex } = this.state;
    const startRenderIndex = this.getStartRenderIndex();
    return (focusedItemIndex - startRenderIndex) * itemHeight - itemHeight;
  }

  getStartRenderIndex() {
    const { renderSize } = this.props;
    const { focusedItemIndex = 0 } = this.state;
    return Math.max(focusedItemIndex - Math.floor((renderSize - 1) / 2), 0);
  }

  getRenderData() {
    const { items, renderSize, infinite, startScrollIndex } = this.props;
    const { focusedItemIndex } = this.state;
    const startIndex = this.getStartRenderIndex();
    const endIndex = startIndex + renderSize;

    if (infinite) {
      const size = items.length;
      const availableRenderSize = Math.min(renderSize, size);
      // Add 1 item left offset for infinite lits if size bigger renderSize
      const start = focusedItemIndex - (renderSize < size ? 1 + startScrollIndex : 0);
      const end = focusedItemIndex + (availableRenderSize - 1);

      const result = [];

      for (let i = start; i <= end; i++) {
        let index = i % size;
        if (index < 0) index = size - Math.abs(index);
        const item = items[index];

        result.push({ item, index });
      }

      return result;
    }

    return items.slice(startIndex, endIndex).map((item, i) => {
      const index = i + startIndex;
      return { item, index };
    });
  }

  render() {
    const { onFocus, onEnter } = this;
    const {
      className,
      listId,
      sectionName,
      defaultSection,
      config,
      itemComponent,
      itemRender,
      onListFocus,
    } = this.props;
    const { focusedItemIndex } = this.state;
    const ItemComponent = itemComponent;

    const data = this.getRenderData();

    return (
      <FocusableParent
        className={className}
        id={listId}
        config={config}
        sectionName={sectionName}
        onFocus={onListFocus}
        defaultSection={defaultSection}
      >
        <ListContentWrapper listOffset={this.calculateOffset()}>
          {data.map(({ item, index }) => {
            const props = {
              key: `${listId}-item-${item.id}`,
              id: `${listId}-item-${item.id}`,
              isActive: focusedItemIndex === index,
              onFocus,
              onEnter,
              index,
              item,
            };
            let result = null;

            if (itemComponent) {
              result = <ItemComponent {...props} />;
            } else if (typeof itemRender === 'function') {
              // Render props pattern: https://reactjs.org/docs/render-props.html
              result = itemRender(props);
            }
            return result;
          })}
        </ListContentWrapper>
      </FocusableParent>
    );
  }
}

VerticalList.defaultProps = {
  listId: 'v1',
  renderSize: 10,
  defaultSection: false,
  defaultIndex: 0,
  startScrollIndex: 0,
};

VerticalList.propTypes = {
  items: PropTypes.array.isRequired,
  itemHeight: PropTypes.number.isRequired,
  renderSize: PropTypes.number.isRequired,
  sectionName: PropTypes.string,
  className: PropTypes.string,
  listId: PropTypes.string,
  defaultIndex: PropTypes.number,
  itemComponent: PropTypes.elementType,
  itemRender: PropTypes.func,
  config: PropTypes.object,
  defaultSection: PropTypes.bool,
};

export default VerticalList;
